import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CrosslistSubnav, PageIntro } from "@/components/SellerToolsNav";

const empty = { name: "", category: "", brand: "", condition: "New", size: "M", description: "", tags: "", shipping_domestic: 8, shipping_international: 25, quantity: 1, hint: "" };

export default function Templates() {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const { data } = useQuery({
    queryKey: ["seller-templates"],
    queryFn: async () => (await api.get("/seller/templates")).data,
  });
  const templates = data?.templates || [];

  const save = async () => {
    try {
      await api.post("/seller/templates", {
        ...form,
        tags: String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        shipping_domestic: Number(form.shipping_domestic) || 0,
        shipping_international: Number(form.shipping_international) || 0,
        quantity: Number(form.quantity) || 1,
      });
      setForm(empty);
      qc.invalidateQueries({ queryKey: ["seller-templates"] });
      toast.success("Template saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/seller/templates/${id}`);
      qc.invalidateQueries({ queryKey: ["seller-templates"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-templates">
      <CrosslistSubnav />
      <PageIntro
        kicker="Crosslist"
        title="Template Manager"
        blurb="Save category, shipping, and copy you reuse. Apply a template when you create an item so similar pieces list faster."
        actions={<Link to="/seller/add" className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg">Create item</Link>}
      />
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {["name", "category", "brand", "condition", "size", "hint"].map((k) => (
          <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={k} className="px-3 py-2 border border-aura-border rounded-lg bg-aura-surface text-sm" />
        ))}
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="sm:col-span-2 px-3 py-2 border border-aura-border rounded-lg bg-aura-surface text-sm min-h-24" />
        <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma separated" className="sm:col-span-2 px-3 py-2 border border-aura-border rounded-lg bg-aura-surface text-sm" />
      </div>
      <button type="button" onClick={save} className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg mb-10">Save template</button>
      {templates.length === 0 ? (
        <div className="text-sm text-aura-muted">Create an item template to pre-fill future listings.</div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="border border-aura-border rounded-lg p-4 flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-aura-muted mt-1">{t.fields?.category || "Any category"} · {t.fields?.brand || "Any brand"}</div>
              </div>
              <div className="flex gap-2">
                <Link to={`/seller/add?template=${t.id}`} className="text-xs px-3 py-1.5 border border-aura-border rounded-lg">Use</Link>
                <button type="button" onClick={() => remove(t.id)} className="text-xs text-red-400">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
